/**
 * The sign-in screen (SEC-03) — the first thing a live deployment shows.
 *
 * Built from the shared controls (Field, Input, Button) rather than bespoke
 * markup, so it inherits the accessibility contract Field now carries: a real
 * <label for>, aria-describedby on the control, and errors announced through
 * role="alert". A hand-rolled login form is exactly where that tends to be
 * skipped, and it is the one form every single user has to get through.
 *
 * ── the split this file exists to hold (2026-08-10) ─────────────────────────
 *
 * Moonbase already ships a considered sign-in at portal.moonbase.be: a brand
 * panel beside the form, the lockup, a headline, an assurance line, a legal
 * footer. Bringing that here could have been done by pasting Moonbase's copy and
 * colours in — and then every customer's portal would greet their consumers with
 * somebody else's identity, which is precisely the leak #125 exists to prevent.
 *
 * So: LAYOUT, STRUCTURE, STATES and ACCESSIBILITY are here, in code. Every
 * IDENTITY fact — logo, headline, tagline, assurance, legal links, footnote — is
 * a prop, and the portal fills them from `portal.branding()` (DATA on the portal
 * row, promotable with the portal module's solution). The console fills them
 * from the deployment brand. Nothing in this file names Moonbase.
 *
 * ── and what happens with nothing set ───────────────────────────────────────
 *
 * Every slot is optional and every one of them degrades to absence rather than
 * to a placeholder. With no branding at all this renders exactly what it
 * rendered before: one centred card, "Sign in", the two fields, the button. The
 * aside is not an empty column — it is not in the document.
 */
import { useId, useState, type FormEvent, type ReactNode } from 'react'
import { BrandLogo, Button, Field, Input, useOptionalLocalization } from '@solum/ui'
import { useInjectedAuth as useAuth } from './provider.js'
import { passwordIssues, passwordScore } from './password.js'
import s from './SignIn.module.css'

/** A link in the legal footer. Rendered only when `href` is http(s) — see below. */
export interface SignInLink { label: string; href: string }

/**
 * A legal link is DATA, arriving from the same jsonb a customer's branding does,
 * and it ends up in an `href`. `javascript:` in an anchor executes in this
 * document, so the same reasoning that made `BrandLogo` coerce a logo to an
 * inert image URI applies to a URL somebody typed into a branding panel: allow
 * the two schemes that can only ever navigate, and drop anything else rather
 * than guess. A relative path is refused too — a portal's legal pages are the
 * operator's site, not a route this SPA has.
 */
export function safeLink(href: string | undefined | null): string | null {
  const v = href?.trim()
  if (!v) return null
  return /^https?:\/\/[^\s]+$/i.test(v) ? v : null
}

export function SignIn({
  title, subtitle, footer, logo, headline, tagline, assurance, legal, footnote,
  onForgotPassword, mode = 'sign-in', onBackToSignIn, redirectTo, defaultEmail,
}: {
  /**
   * Show "Forgot your password?" and call this when it is pressed.
   *
   * ABSENT BY DEFAULT, and that is the point rather than an omission. Whether a
   * deployment offers a password reset is CONFIGURATION — `portal.
   * registration_options().password_reset`, which defaults to off wherever an
   * `access.idp_connection` is enabled. On an Entra-only portal the affordance
   * is a false promise: everyone who follows it spends a support ticket
   * discovering that their organisation owns their sign-in. A link that is
   * always drawn is a link that lies on some deployments.
   *
   * It is also why this is a CALLBACK and not an `href`. This component is
   * shared by the portal, the console and the extension; the portal has no
   * router, the console's gate sits outside one, and the extension has no
   * navigable location at all. Each host decides what "go to the reset screen"
   * means for it.
   */
  onForgotPassword?: () => void
  /** Signup is opt-in: hosts must never expose it merely because auth supports it. */
  mode?: 'sign-in' | 'sign-up'
  onBackToSignIn?: () => void
  /** GoTrue's allowlisted landing after email confirmation. */
  redirectTo?: string
  /** Override the heading — a white-labelled portal is not "Moonbase". */
  title?: string
  /**
   * Override the subtitle. The default is deliberately brand-neutral: it used to
   * read "Use your Moonbase account", which on a white-labelled portal is
   * exactly the leak #125 exists to prevent, and it was the fallback on every
   * portal that had not set a name.
   */
  subtitle?: string
  /**
   * Rendered under the submit button. A slot rather than props for
   * registration/IdP buttons, because this component is shared by the console
   * and the portal and must not learn what a portal is.
   */
  footer?: ReactNode
  /** The brand's own logo — markup or an image URI. Inert either way (BrandLogo). */
  logo?: string | null
  /**
   * The brand-panel headline. A newline is a line break, so a two-line lockup
   * ("Welcome to / the base.") is expressible as data without the data carrying
   * markup. The last line takes the brand gradient.
   */
  headline?: string | null
  /** One sentence under the headline. */
  tagline?: string | null
  /** The standing line at the foot of the brand panel — hosting, posture. */
  assurance?: string | null
  /** Privacy / terms / cookies. Non-http(s) entries are dropped, not rendered. */
  legal?: SignInLink[] | null
  /** The small line under the form — host and copyright. */
  footnote?: string | null
  /**
   * Pre-fill the address field (#1053 4b, the account switcher).
   *
   * A SWITCH is a sign-out followed by a fresh authentication — nothing is
   * parked, so the switcher can only ever offer, never grant — and the one
   * thing the device legitimately knows is which address the person chose.
   * Typing it back is friction with no security value: the field stays fully
   * editable, so somebody who changes their mind signs in as whoever they like.
   *
   * A DEFAULT and not a controlled value: it seeds the initial state once, so a
   * host re-rendering does not overwrite what the person is halfway through
   * typing.
   */
  defaultEmail?: string | null
}) {
  const { signIn, signUp } = useAuth()
  const localization = useOptionalLocalization()
  const message = (key: string, fallback: string) => localization?.message(key, fallback) ?? fallback
  const [email, setEmail] = useState(defaultEmail ?? '')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [confirmationSent, setConfirmationSent] = useState(false)
  const errorId = useId()

  async function submit(e: FormEvent) {
    e.preventDefault()
    setError(null)
    // Validated here rather than by disabling the button. A disabled submit is
    // the classic login trap: nothing announces WHY it cannot be pressed, and a
    // screen-reader user gets a control that is simply not there. The button
    // stays operable and the form says what is missing, in the same alert
    // region a wrong password uses.
    if (!email.trim() || !password) {
      setError(mode === 'sign-up'
        ? message('auth.validation.signup_credentials_required', 'Enter your email address and choose a password.')
        : message('auth.validation.credentials_required', 'Enter your email address and password.'))
      return
    }
    if (mode === 'sign-up') {
      if (password !== confirmPassword) {
        setError(message('auth.validation.passwords_mismatch', 'The two passwords do not match.'))
        return
      }
      const issues = passwordIssues(password, { email: email.trim() })
      if (issues.length > 0) {
        setError(issues[0]!.message)
        return
      }
    }
    setBusy(true)
    try {
      if (mode === 'sign-up') {
        const result = await signUp(email.trim(), password, redirectTo)
        if (result.confirmationRequired) setConfirmationSent(true)
      } else {
        await signIn(email.trim(), password)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : (mode === 'sign-up'
        ? message('auth.error.sign_up_failed', 'Could not create your account.')
        : message('auth.error.sign_in_failed', 'Sign-in failed.')))
    } finally {
      setBusy(false)
    }
  }

  // The aside is content, not decoration, so it is absent rather than empty when
  // a deployment has nothing to put in it. `hasAside` also drives the layout:
  // one column becomes two only when there is a second column to draw.
  const hasAside = Boolean(headline || tagline || assurance || logo)
  const links = (legal ?? [])
    .map((l) => ({ label: l.label, href: safeLink(l.href) }))
    .filter((l): l is SignInLink => Boolean(l.href && l.label))

  // The headline's last line carries the gradient — the same treatment the
  // primary button gets, so the two agree without either naming a colour.
  const headlineLines = (headline ?? '').split('\n').map((l) => l.trim()).filter(Boolean)

  return (
    <div className={s.page} data-aside={hasAside || undefined}>
      {hasAside && (
        // Not a landmark and not in the tab order: it is the brand's statement,
        // and a keyboard user's first Tab must land in the email field.
        <aside className={s.aside} data-testid="signin-aside">
          {/* Two washes of the brand's own colours. Painted in CSS from
              --brand/--accent rather than from an inline style, so a brand swap
              moves them and no custom property is set from JSX. */}
          <span className={s.glowBrand} aria-hidden="true" />
          <span className={s.glowAccent} aria-hidden="true" />
          <div className={s.asideInner}>
            {logo && <BrandLogo logo={logo} className={s.asideLogo} testId="signin-logo" />}
            {/* A paragraph, not an <h2>. It reads like a heading and it is not
                one: it names no section, and it sits BEFORE the form's <h1> in
                the document, so marking it up as a heading would put the page's
                first heading at level 2 and its second at level 1 — the
                heading-order failure, for a line of brand copy. */}
            {headlineLines.length > 0 && (
              <p className={s.headline}>
                {headlineLines.map((line, i) => (
                  <span key={`${i}-${line}`} className={i === headlineLines.length - 1 ? s.headlineAccent : undefined}>
                    {line}
                    {i < headlineLines.length - 1 && <br />}
                  </span>
                ))}
              </p>
            )}
            {tagline && <p className={s.tagline}>{tagline}</p>}
            {assurance && <p className={s.assurance} data-testid="signin-assurance">{assurance}</p>}
          </div>
        </aside>
      )}

      <div className={s.formSide}>
        {/* A form element, not a div with a click handler: Enter must submit, and
            password managers key off a real form. */}
        <form className={s.card} onSubmit={submit} noValidate>
          {/* On a narrow viewport the aside is not rendered, so the lockup comes
              with the form — otherwise a phone gets an unbranded login on a
              branded portal. */}
          {logo && <BrandLogo logo={logo} className={s.cardLogo} testId="signin-logo-compact" />}
          {/* A deployment that declares a NAME and no logo was getting an
              unbranded phone login — the exact thing the comment above says
              this lockup exists to prevent, except the protection was
              logo-only. Measured in the browser on 390x844 (#2296): the aside
              is not rendered at this width, the compact lockup drew nothing,
              and the only heading was the generic "Sign in".

              The headline is the governed public NAME (the anonymous brand
              projection allows no free text), so this is identity, not
              decoration — and it is the half that survives when a logo is
              missing or broken, which is what Root's ruling requires. */}
          {!logo && headline && (
            <p className={s.cardBrand} data-testid="signin-brand-compact">{headlineLines[0] ?? headline}</p>
          )}
          <h1 className={s.title}>{title ?? (mode === 'sign-up'
            ? message('auth.sign_up.title', 'Create your account')
            : message('auth.sign_in.title', 'Sign in'))}</h1>
          <p className={s.sub}>{subtitle ?? (mode === 'sign-up'
            ? message('auth.sign_up.subtitle', 'Use an address you can open to confirm your account.')
            : message('auth.sign_in.subtitle', 'Use your account to continue.'))}</p>

          {confirmationSent ? (
            <div className={s.confirmation} role="status" data-testid="signup-confirmation">
              <strong>{message('auth.sign_up.confirmation.title', 'Check your email')}</strong>
              <span>{message('auth.sign_up.confirmation.detail', 'If an account can be created for that address, its confirmation link is on the way.')}</span>
              {onBackToSignIn && (
                <button type="button" className={s.forgot} onClick={onBackToSignIn}>
                  {message('auth.action.back_to_sign_in', 'Back to sign in')}
                </button>
              )}
            </div>
          ) : <>

          {/* role=alert so the failure is ANNOUNCED, not merely displayed — a
              screen-reader user submitting a wrong password otherwise gets
              silence. It sits ABOVE the fields and both of them point at it
              (aria-describedby), so the message is reachable from the control a
              person is about to correct rather than only from the bottom of the
              form. */}
          {error && <div className={s.error} id={errorId} role="alert">{error}</div>}

          <Field label={message('auth.field.email', 'Email')} required>
            <Input
              type="email"
              value={email}
              autoComplete={mode === 'sign-up' ? 'email' : 'username'}
              autoFocus
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              onChange={(e) => setEmail(e.target.value)}
            />
          </Field>

          <Field label={message('auth.field.password', 'Password')} required>
            <Input
              type="password"
              value={password}
              autoComplete={mode === 'sign-up' ? 'new-password' : 'current-password'}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? errorId : undefined}
              onChange={(e) => setPassword(e.target.value)}
            />
          </Field>

          {mode === 'sign-up' && password && (
            <div className={s.meter}>
              <progress max={4} value={passwordScore(password, { email })}
                aria-label={message('auth.password.strength', 'Password strength')} />
              <span>{message('auth.password.advice', 'Use at least 12 characters; a few ordinary words work well.')}</span>
            </div>
          )}

          {mode === 'sign-up' && (
            <Field label={message('auth.field.confirm_password', 'Confirm password')} required>
              <Input
                type="password"
                value={confirmPassword}
                autoComplete="new-password"
                aria-invalid={error ? true : undefined}
                aria-describedby={error ? errorId : undefined}
                onChange={(e) => setConfirmPassword(e.target.value)}
              />
            </Field>
          )}

          <Button type="submit" variant="primary" loading={busy} disabled={busy}>
            {mode === 'sign-up'
              ? message('auth.action.create_account', 'Create account')
              : message('auth.action.sign_in', 'Sign in')}
          </Button>

          {/* Under the button, not beside the password field. A person reaches
              for this AFTER a failed attempt, and the reading order that matches
              is "try, fail, find the way out" — putting it above the button also
              puts it in the tab path between the last field and submit, which
              is where a keyboard user least wants a link. */}
          {mode === 'sign-in' && onForgotPassword && (
            <button type="button" className={s.forgot} onClick={onForgotPassword}
              data-testid="signin-forgot">
              {message('auth.action.forgot_password', 'Forgot your password?')}
            </button>
          )}

          {mode === 'sign-up' && onBackToSignIn && (
            <button type="button" className={s.forgot} onClick={onBackToSignIn}>
              {message('auth.action.back_to_sign_in', 'Back to sign in')}
            </button>
          )}

          {mode === 'sign-in' && footer}
          </>}
        </form>

        {(links.length > 0 || footnote) && (
          <div className={s.legal}>
            {links.length > 0 && (
              <nav className={s.legalLinks} aria-label={message('auth.legal.label', 'Legal')}>
                {links.map((l) => (
                  // rel: these are the operator's own pages on another origin,
                  // and this document holds a session.
                  <a key={l.href} href={l.href} target="_blank" rel="noopener noreferrer">{l.label}</a>
                ))}
              </nav>
            )}
            {footnote && <p className={s.footnote} data-testid="signin-footnote">{footnote}</p>}
          </div>
        )}
      </div>
    </div>
  )
}
